import React, { useState } from 'react';
import { Check, Users, TrendingUp, Zap, Shield, Award, Heart, Download, Send, Leaf, Target, Home, UserMinus, Briefcase, Baby, RefreshCw, Gift, Rocket } from 'lucide-react';
import axios from 'axios';
import SEO, { SEOPages } from '../components/SEO';

const MindFinance = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    email: '',
    company_size: '',
    request_type: '',
    job_title: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const API_BASE = '/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axios.post(`${API_BASE}/forms/mindfinance`, {
        ...formData,
        url_referrer: window.location.href,
        utm_source: new URLSearchParams(window.location.search).get('utm_source'),
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
      });

      if (response.data.success) {
        setSubmitSuccess(true);
        setFormData({
          full_name: '',
          company: '',
          email: '',
          company_size: '',
          request_type: '',
          job_title: '',
          message: '',
          consent: false
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    // Ouvrir le guide HTML dans un nouvel onglet (l'utilisateur peut l'imprimer en PDF)
    const guideUrl = '/assets/pdf/Guide_MindFinance_V2.html';
    window.open(guideUrl, '_blank');
  };

  return (
    <>
      <SEO {...SEOPages.mindfinance} />
      <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          role="img"
          aria-label="Programme bien-être financier MindFinance - employés heureux et sereins au travail"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxlbXBsb3llZXxlbnwwfHx8fDE3NjE4NDA5MjJ8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.4)'
          }}
        />
        
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/50 to-[#0C1D3D]/80 z-10" />
        
        <div className="relative z-20 max-w-6xl mx-auto px-4 md:px-6 text-center">
          <div className="glass p-6 md:p-12 rounded-3xl">
            <div className="flex flex-col md:flex-row items-center justify-center mb-4 md:mb-6">
              <Leaf className="text-green-400 mb-2 md:mb-0 md:mr-3" size={40} />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white">
                ATOM MindFinance
              </h1>
            </div>
            
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-blue-200 mb-3 md:mb-4">
              Le bien-être financier en entreprise
            </p>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-6 md:mb-10 px-2">
              Quand les finances vont bien, l'esprit travaille mieux.<br className="hidden sm:block"/>
              Le premier programme suisse qui aide vos collaborateurs à réduire leur stress financier et à retrouver la sérénité.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#concept" 
                className="glass-button bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transition-all"
              >
                Découvrir le programme
              </a>
              <a 
                href="#contact" 
                className="glass-button bg-white/10 backdrop-blur-md text-white border-2 border-white/30 px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all"
              >
                Demander une présentation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Le problème */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C1D3D] mb-4 md:mb-6 px-2">
              Le stress financier, un frein invisible à la performance
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                Chaque mois, des collaborateurs suisses subissent un stress lié à leurs finances personnelles.
              </p>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-6">
                Incompréhension de la LPP, charges privées mal maîtrisées, peur du futur : des facteurs invisibles qui affectent la <strong>motivation</strong>, la <strong>santé mentale</strong> et la <strong>fidélité</strong>.
              </p>
              <p className="text-lg md:text-xl font-semibold text-blue-600">
                ATOM MindFinance apporte une réponse simple, humaine et mesurable à ce problème.
              </p>
            </div>
            
            <div className="relative mt-6 md:mt-0">
              <img 
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxlbXBsb3llZXxlbnwwfHx8fDE3NjE4NDA5MjJ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Employés heureux au travail"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Le concept */}
      <section id="concept" className="py-12 md:py-20 bg-[#0C1D3D] text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 px-2">
              Une approche unique à deux niveaux
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 md:gap-12">
            <div className="glass p-6 md:p-8 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-blue-500/20 flex items-center justify-center mb-3 sm:mb-0 sm:mr-4 flex-shrink-0">
                  <Users size={24} className="text-blue-400 md:w-8 md:h-8" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold">1️⃣ Bien-être collectif</h3>
              </div>
              <p className="text-gray-200 text-base md:text-lg leading-relaxed">
                Un espace de clarté et d'écoute pour tous les collaborateurs — comprendre enfin sa LPP, ses assurances, son budget et ses impôts, <strong>sans jargon ni vente</strong>.
              </p>
            </div>
            
            <div className="glass p-6 md:p-8 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 md:mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-3 sm:mb-0 sm:mr-4 flex-shrink-0">
                  <Heart size={24} className="text-green-400 md:w-8 md:h-8" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold">2️⃣ Accompagnement individuel</h3>
              </div>
              <p className="text-gray-200 text-base md:text-lg leading-relaxed">
                Pour ceux qui souhaitent aller plus loin : un audit complet, une optimisation de leurs charges privées et un plan de sérénité financière durable.
              </p>
            </div>
          </div>
          
          <div className="mt-8 md:mt-12 text-center px-4">
            <img 
              src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=1200&q=80" 
              alt="Collaboration"
              className="rounded-2xl shadow-2xl mx-auto w-full max-w-3xl"
            />
          </div>
        </div>
      </section>

      {/* Section 3: Bénéfices entreprise */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C1D3D] mb-4 md:mb-6 px-2">
              Pourquoi les entreprises l'adoptent
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              ATOM MindFinance transforme un sujet anxiogène en un levier positif de culture d'entreprise.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: TrendingUp, title: "Réduit le stress et le turnover", desc: "Des collaborateurs plus sereins restent plus longtemps" },
              { icon: Award, title: "Fidélise les collaborateurs", desc: "Un avantage RH différenciant et valorisant" },
              { icon: Heart, title: "Valorise la marque employeur", desc: "Montrez que vous vous souciez vraiment de vos équipes" },
              { icon: Zap, title: "Aucun déploiement IT", desc: "Solution clé en main, activation en 24h" },
              { icon: Check, title: "100% déductible fiscalement", desc: "Optimisation fiscale pour l'entreprise" },
              { icon: Shield, title: "Image innovante et bienveillante", desc: "Positionnement avant-gardiste sur le bien-être" }
            ].map((benefit, index) => (
              <div key={index} className="glass p-5 md:p-6 rounded-2xl hover:scale-105 transition-all">
                <benefit.icon className="text-blue-500 mb-3 md:mb-4" size={32} />
                <h3 className="text-lg md:text-xl font-bold text-[#0C1D3D] mb-2 md:mb-3">{benefit.title}</h3>
                <p className="text-sm md:text-base text-gray-600">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3.5: Les chiffres du stress financier */}
      <section className="py-12 md:py-20 bg-[#0C1D3D] text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 px-2">
              📊 Les chiffres du stress financier en Suisse
            </h2>
            <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
              Les données parlent d'elles-mêmes : le stress financier est un problème massif et coûteux pour les entreprises suisses.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-10">
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="text-5xl md:text-6xl font-bold text-blue-400 mb-4">58%</div>
              <p className="text-base md:text-lg text-gray-200 leading-relaxed">
                des employés suisses éprouvent des difficultés financières
              </p>
              <p className="text-xs md:text-sm text-gray-400 mt-3">Source: PwC Hopes & Fears, 2023</p>
            </div>
            
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="text-4xl md:text-5xl font-bold text-green-400 mb-4">-27%</div>
              <p className="text-base md:text-lg text-gray-200 leading-relaxed">
                de réduction de l'absentéisme avec un programme de bien-être financier
              </p>
              <p className="text-xs md:text-sm text-gray-400 mt-3">Source: Études bien-être entreprise</p>
            </div>
            
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="text-5xl md:text-6xl font-bold text-red-400 mb-4">40%</div>
              <p className="text-base md:text-lg text-gray-200 leading-relaxed">
                des démissions en Suisse sont liées à des problèmes financiers
              </p>
              <p className="text-xs md:text-sm text-gray-400 mt-3">Source: Randstad Employer Brand 2024</p>
            </div>
          </div>
          
          <div className="glass p-6 md:p-8 rounded-2xl bg-gradient-to-r from-green-500/20 to-blue-500/20 border-2 border-green-400/30">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0 text-center md:text-left">
                <p className="text-xl md:text-2xl font-bold text-white mb-2">
                  💡 Et si vous agissiez maintenant ?
                </p>
                <p className="text-base md:text-lg text-gray-200">
                  En réduisant le stress financier, vous diminuez <strong className="text-green-300">l'absentéisme, le turnover et les démissions</strong>. Chaque collaborateur retenu = des dizaines de milliers de francs économisés.
                </p>
              </div>
              <a 
                href="#contact" 
                className="glass-button bg-white text-[#0C1D3D] px-6 md:px-8 py-3 md:py-4 rounded-full font-bold hover:scale-105 transition-all whitespace-nowrap"
              >
                Demander une démo
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section Crédibilité - Pourquoi nous faire confiance */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#0C1D3D] to-black">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Pourquoi faire confiance à ATOM MindFinance ?
          </h2>
          <p className="text-center text-gray-300 text-base md:text-lg mb-12 max-w-3xl mx-auto px-2">
            Parce que nous ne sommes pas des "coachs bien-être" improvisés. 
            Nous sommes des <strong className="text-blue-400">professionnels de la finance certifiés</strong> avec plus de 24 ans d'expérience combinée.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {/* Joël Ott */}
            <div className="glass p-6 md:p-8 rounded-2xl">
              <div className="flex items-center mb-4">
                <img 
                  src="/assets/team/joel-ott.jpg" 
                  alt="Joël Ott" 
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover mr-4 flex-shrink-0"
                />
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Joël Ott</h3>
                  <p className="text-blue-300 text-sm md:text-base">Co-Fondateur ATOM Group SA</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm md:text-base mb-4">
                Entrepreneur visionnaire avec plus de 16 ans d'expérience dans le conseil financier, Joël a dirigé des équipes de grande ampleur, piloté des projets à forte croissance et fondé plusieurs entreprises en Suisse romande. Il se distingue par une approche moderne, humaine et orientée résultats, qu'il met aujourd'hui au service du développement d'ATOM Group SA.
              </p>
              <ul className="space-y-2 text-gray-300 text-sm md:text-base">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Swiss Life Select (2009-2025) – Top Management</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Entrepreneur multi-secteurs (CareWash, D.A.D Services, Elevatis SA)</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Direction, formation & coaching de plus de 50 conseillers</span>
                </li>
              </ul>
              <a 
                href="https://www.linkedin.com/in/joelottfinance/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                Voir le profil LinkedIn
              </a>
            </div>

            {/* Quentin Mauron */}
            <div className="glass p-6 md:p-8 rounded-2xl">
              <div className="flex items-center mb-4">
                <img 
                  src="/assets/team/quentin-mauron.png"
                  alt="Quentin Mauron" 
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover mr-4 flex-shrink-0"
                />
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Quentin Mauron</h3>
                  <p className="text-green-300 text-sm md:text-base">Co-Fondateur ATOM Group SA</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm md:text-base mb-4">
                Ancien manager régional et spécialiste en planification financière globale, Quentin allie leadership humain, expertise technique et culture du coaching. Fort d'une expérience auprès de clients privés et PME ainsi que d'une activité entrepreneuriale dans l'immobilier, il met aujourd'hui sa vision complète et structurée au service de la croissance d'ATOM Group SA.
              </p>
              <ul className="space-y-2 text-gray-300 text-sm md:text-base">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Brevet fédéral de conseiller financier</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Intervenant HEG Arc</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 mt-1 flex-shrink-0" size={16} />
                  <span>Fondateur de Puramis SA – Gestion immobilière</span>
                </li>
              </ul>
              <a 
                href="https://www.linkedin.com/in/quentinmauron/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center text-blue-400 hover:text-blue-300 text-sm transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                Voir le profil LinkedIn
              </a>
            </div>
          </div>

          {/* Agréments */}
          <div className="glass p-6 md:p-8 rounded-2xl text-center">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
              🛡️ Agréments officiels & conformité
            </h3>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-4">
              <div className="px-4 md:px-6 py-2 md:py-3 bg-green-500/20 rounded-full text-green-300 text-xs md:text-sm font-semibold">
                ✓ Conforme LSFIN
              </div>
              <div className="px-4 md:px-6 py-2 md:py-3 bg-blue-500/20 rounded-full text-blue-300 text-xs md:text-sm font-semibold">
                ✓ Enregistré FINMA
              </div>
            </div>
            <p className="text-gray-400 text-xs md:text-sm px-2">
              Intermédiaire d'assurance non lié, conforme à la Loi sur les Services Financiers
            </p>
            <div className="mt-4">
              <a 
                href="/a-propos"
                className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
              >
                En savoir plus sur notre expertise
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section Processus - Comment ça marche */}
      <section id="processus" className="py-16 md:py-20 bg-gradient-to-b from-black to-[#0C1D3D]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Comment ça fonctionne concrètement ?
          </h2>
          <p className="text-center text-gray-300 text-base md:text-lg mb-12 max-w-3xl mx-auto px-2">
            Un processus simple, confidentiel et 100% accessible en ligne
          </p>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {/* Étape 1 */}
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                1
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Accès simple et confidentiel</h3>
              <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed">
                Vos collaborateurs reçoivent un lien d'accès personnalisé. <strong className="text-blue-400">Confidentialité totale garantie : aucune donnée nominative transmise à l'employeur.</strong>
              </p>
              <div className="text-sm text-gray-400 bg-gray-800/50 p-3 rounded-lg">
                <strong className="text-white">Langues disponibles :</strong><br/>
                🇫🇷 Français • 🇩🇪 Allemand • 🇬🇧 Anglais
              </div>
            </div>

            {/* Étape 2 */}
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                2
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Diagnostic & Plan d'action</h3>
              <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed">
                1 à 3 séances avec un conseiller financier certifié (visio ou téléphone). Analyse de la situation et plan budgétaire concret.
              </p>
              <div className="text-sm text-gray-400 bg-gray-800/50 p-3 rounded-lg">
                <strong className="text-white">Durée moyenne :</strong><br/>
                2 à 4 semaines
              </div>
            </div>

            {/* Étape 3 */}
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-transform">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                3
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">Suivi & Soutien</h3>
              <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed">
                Accès à notre plateforme d'outils budgétaires + possibilité de recontacter le conseiller régulièrement.
              </p>
              <div className="text-sm text-gray-400 bg-gray-800/50 p-3 rounded-lg">
                <strong className="text-white">Support continu :</strong><br/>
                À chaque changement de situation ou question
              </div>
            </div>
          </div>

          {/* Confidentialité - Encadré spécial */}
          <div className="glass p-6 md:p-8 rounded-2xl bg-gradient-to-r from-green-500/10 to-blue-500/10 border-2 border-green-400/30">
            <div className="flex flex-col md:flex-row items-start md:items-center">
              <Shield className="text-green-400 mr-0 md:mr-4 mb-4 md:mb-0 flex-shrink-0 mx-auto md:mx-0" size={48} />
              <div className="text-center md:text-left">
                <h4 className="text-lg md:text-xl font-bold text-white mb-2">
                  🔒 Confidentialité absolue garantie
                </h4>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                  <strong className="text-green-300">L'employeur ne reçoit AUCUNE donnée individuelle.</strong> Seuls des indicateurs anonymisés sont partagés :
                  taux de participation, satisfaction globale, thématiques récurrentes (sans noms ni informations personnelles).
                </p>
                <p className="text-gray-400 text-xs md:text-sm mt-3 italic">
                  Conforme RGPD et Loi fédérale sur la protection des données (LPD)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Pack Sérénité - DÉTAILLÉ */}
      <section className="py-12 md:py-20 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0C1D3D] mb-4">
              💰 Pack Sérénité Individuelle
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto px-2">
              Ce que reçoit chaque collaborateur avec le programme ATOM MindFinance
            </p>
          </div>

          {/* Prix barré + Offert */}
          <div className="relative mb-8">
            {/* Badge "INCLUS" en haut à droite */}
            <div className="absolute -top-4 -right-4 z-10 bg-green-500 text-white px-6 py-2 rounded-full font-bold text-sm md:text-base shadow-lg transform rotate-12">
              ✓ INCLUS
            </div>
            
            <div className="p-6 md:p-8 rounded-3xl text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl">
              <p className="text-sm md:text-base uppercase tracking-wide mb-2 text-blue-100">Pack Sérénité complet</p>
              <div className="flex items-center justify-center gap-4 mb-2">
                <p className="text-4xl md:text-5xl font-bold text-white">INCLUS</p>
              </div>
              <p className="text-base md:text-lg text-green-200 font-semibold">Offert dans l'abonnement MindFinance entreprise</p>
            </div>
          </div>

          {/* Explication claire du modèle */}
          <div className="glass p-6 md:p-8 rounded-2xl bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#0C1D3D] mb-4 text-center">
              🎁 Comment ça marche ?
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-5 rounded-xl border-2 border-gray-200">
                <h4 className="font-bold text-lg text-gray-800 mb-3 flex items-center">
                  <span className="bg-red-100 text-red-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">❌</span>
                  Sans MindFinance
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Le collaborateur paie le coaching de sa poche</li>
                  <li>• Aucun suivi collectif</li>
                  <li>• Démarche individuelle isolée</li>
                </ul>
              </div>
              
              <div className="bg-white p-5 rounded-xl border-2 border-green-400 shadow-lg">
                <h4 className="font-bold text-lg text-green-700 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-700 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">✓</span>
                  Avec MindFinance
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• L'entreprise offre le programme à tous</li>
                  <li>• Pack Sérénité <strong className="text-green-600">INCLUS</strong> pour chaque employé</li>
                  <li>• Déploiement collectif structuré</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <p className="text-sm md:text-base text-gray-800 text-center">
                <strong>💡 Valeur perçue par l'employé :</strong> Programme complet offert par l'employeur = 
                <strong className="text-green-600"> Marque employeur renforcée</strong>
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8">
            {/* Le processus en 3 étapes */}
            <div className="glass p-6 md:p-8 rounded-2xl bg-white">
              <h3 className="text-xl md:text-2xl font-bold text-[#0C1D3D] mb-4 flex items-center">
                <Users className="text-blue-500 mr-3" size={28} />
                Le processus en 3 étapes
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3 mt-1">1</div>
                  <div>
                    <strong className="text-gray-900">Analyse initiale</strong>
                    <p className="text-sm text-gray-600">Diagnostic financier complet : budget, dettes, épargne, LPP, assurances (1 séance de 45-60 min)</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3 mt-1">2</div>
                  <div>
                    <strong className="text-gray-900">Conseil & Plan d'action</strong>
                    <p className="text-sm text-gray-600">Remise d'un plan d'action personnalisé avec objectifs chiffrés et recommandations concrètes (Document PDF)</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold mr-3 mt-1">3</div>
                  <div>
                    <strong className="text-gray-900">Suivi régulier tous les 12 mois</strong>
                    <p className="text-sm text-gray-600">Séance de suivi annuelle pour faire le point, ajuster le plan et mesurer les progrès accomplis</p>
                  </div>
                </li>
              </ul>
              <div className="mt-4 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm text-gray-800">
                  <strong>🔄 Accompagnement dans la durée :</strong> Le suivi annuel garantit que votre situation financière reste sous contrôle année après année.
                </p>
              </div>
            </div>

            {/* Outils et supports */}
            <div className="glass p-6 md:p-8 rounded-2xl bg-white">
              <h3 className="text-xl md:text-2xl font-bold text-[#0C1D3D] mb-4 flex items-center">
                <Award className="text-green-500 mr-3" size={28} />
                Outils & Supports
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border-2 border-green-300">
                  <Check className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <strong className="text-gray-900">👨‍👩‍👧 Extension Familiale INCLUSE</strong>
                    <p className="text-sm text-gray-700"><strong>Conjoint/Partenaire peut aussi bénéficier du coaching</strong> sans surcoût. Les finances de couple se gèrent à deux !</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-3 flex-shrink-0 mt-1" size={18} />
                  <div>
                    <strong className="text-gray-900">Templates budgétaires</strong>
                    <p className="text-sm text-gray-600">Excel et Google Sheets prêts à l'emploi</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-3 flex-shrink-0 mt-1" size={18} />
                  <div>
                    <strong className="text-gray-900">Guide d'optimisation des charges</strong>
                    <p className="text-sm text-gray-600">Assurances, LPP, fiscalité</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-3 flex-shrink-0 mt-1" size={18} />
                  <div>
                    <strong className="text-gray-900">Accès plateforme en ligne</strong>
                    <p className="text-sm text-gray-600">Ressources et outils disponibles 24/7</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 mr-3 flex-shrink-0 mt-1" size={18} />
                  <div>
                    <strong className="text-gray-900">Simulateurs financiers</strong>
                    <p className="text-sm text-gray-600">LPP, 3ème pilier, fiscalité, retraite</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Support entre les suivis */}
          <div className="glass p-6 md:p-8 rounded-2xl bg-white mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#0C1D3D] mb-4 flex items-center">
              <Shield className="text-purple-500 mr-3" size={28} />
              Support continu entre les suivis annuels
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-purple-600 font-bold mb-2">📧 Support par email</div>
                <p className="text-sm text-gray-700">Questions ponctuelles possibles par email entre deux suivis annuels</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-purple-600 font-bold mb-2">📞 Point rapide si nécessaire</div>
                <p className="text-sm text-gray-700">Possibilité de rappel court (15 min) en cas de situation urgente</p>
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border-2 border-green-300">
              <div className="flex items-start">
                <Award className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-sm text-gray-800">
                  <strong>Un accompagnement sur le long terme :</strong> Le Pack Sérénité offre bien plus que quelques séances isolées : c'est un partenariat durable avec un suivi annuel régulier à vie.
                </p>
              </div>
            </div>
          </div>

          {/* Avantages du modèle tout inclus */}
          <div className="glass p-6 md:p-8 rounded-2xl bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300">
            <h3 className="text-xl md:text-2xl font-bold text-[#0C1D3D] mb-4 flex items-center text-center">
              <TrendingUp className="text-green-600 mr-3" size={28} />
              Pourquoi ce modèle est gagnant-gagnant ?
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg border border-green-200">
                <div className="font-bold text-green-700 mb-2">👔 Pour l'entreprise</div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ Offre sur mesure personnalisée</li>
                  <li>✓ 100% déductible fiscalement</li>
                  <li>✓ Marque employeur renforcée</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-blue-200">
                <div className="font-bold text-blue-700 mb-2">😊 Pour l'employé</div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ Programme complet offert</li>
                  <li>✓ Suivi à vie inclus</li>
                  <li>✓ <strong>Conjoint/Partenaire inclus</strong></li>
                  <li>✓ Confidentialité totale</li>
                  <li>✓ Aucun frais personnel</li>
                </ul>
              </div>
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <div className="font-bold text-purple-700 mb-2">🎯 Pour le DRH</div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ Déploiement simple</li>
                  <li>✓ Pas de gestion individuelle</li>
                  <li>✓ Impact collectif immédiat</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 bg-white p-4 rounded-lg border-2 border-green-400 text-center">
              <p className="text-sm md:text-base text-gray-800">
                <strong>💡 Le modèle clé en main :</strong> Pas de gestion individuelle des packs, pas de validation au cas par cas. 
                L'entreprise souscrit → Tous les employés en bénéficient automatiquement.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Section Grands Événements de Vie */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-purple-900 via-blue-900 to-[#0C1D3D]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Target className="text-green-400 mr-3" size={40} />
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Programme Grands Événements de Vie
              </h2>
            </div>
            <p className="text-lg md:text-xl text-blue-200 max-w-3xl mx-auto mb-6">
              Un accompagnement renforcé lors des moments clés où vos finances sont bouleversées
            </p>
            <div className="inline-block bg-gradient-to-r from-green-400 to-blue-400 text-white px-6 py-3 rounded-full font-bold text-sm md:text-base">
              ✨ INCLUS SANS SURCOÛT dans le programme
            </div>
          </div>

          {/* Statistique d'impact */}
          <div className="glass p-6 rounded-2xl mb-12 border border-blue-400 bg-white/10">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-green-300 mb-2">73%</div>
              <p className="text-white text-base md:text-lg">
                des Suisses vivent au moins <strong>1 de ces événements</strong> tous les 5 ans
              </p>
              <p className="text-blue-200 text-sm mt-2">
                Source : Études comportement financier Suisse, 2024
              </p>
            </div>
          </div>

          {/* Les 5 événements */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            
            {/* Événement 1 - Achat Immobilier */}
            <div className="glass p-6 rounded-2xl bg-white/5 border border-blue-300 hover:border-green-400 transition-all hover:scale-105">
              <div className="flex justify-center mb-4">
                <Home className="text-green-400" size={48} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 text-center">Achat Immobilier</h3>
              <p className="text-blue-200 text-sm mb-4 text-center">3-4 séances sur 6 mois</p>
              <ul className="space-y-2 text-sm text-white">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Calcul capacité d'emprunt réelle</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Négociation hypothèque (comparaison banques et assurances)</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Optimisation LPP + 3e pilier pour apport</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Restructuration budget post-achat</span>
                </li>
              </ul>
              <div className="mt-4 bg-green-500/20 border border-green-400 rounded-lg p-3">
                <p className="text-sm text-green-300 font-semibold">💰 Économies potentielles significatives</p>
              </div>
            </div>

            {/* Événement 2 - Divorce */}
            <div className="glass p-6 rounded-2xl bg-white/5 border border-blue-300 hover:border-green-400 transition-all hover:scale-105">
              <div className="flex justify-center mb-4">
                <UserMinus className="text-purple-400" size={48} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 text-center">Divorce/Séparation</h3>
              <p className="text-blue-200 text-sm mb-4 text-center">4-5 séances sur 12 mois</p>
              <ul className="space-y-2 text-sm text-white">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Inventaire patrimoine + partage LPP</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Calcul pension alimentaire optimisé</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Préparation dossier avocat/juge</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Plan reconstruction financière</span>
                </li>
              </ul>
              <div className="mt-4 bg-green-500/20 border border-green-400 rounded-lg p-3">
                <p className="text-sm text-green-300 font-semibold">💰 Optimisation substantielle possible</p>
              </div>
            </div>

            {/* Événement 3 - Héritage */}
            <div className="glass p-6 rounded-2xl bg-white/5 border border-blue-300 hover:border-green-400 transition-all hover:scale-105">
              <div className="flex justify-center mb-4">
                <Briefcase className="text-blue-400" size={48} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 text-center">Héritage/Succession</h3>
              <p className="text-blue-200 text-sm mb-4 text-center">2-3 séances sur 3-6 mois</p>
              <ul className="space-y-2 text-sm text-white">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Analyse acceptation succession</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Optimisation fiscale (droits succession)</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Stratégie placement montants reçus</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Planification propre succession</span>
                </li>
              </ul>
              <div className="mt-4 bg-green-500/20 border border-green-400 rounded-lg p-3">
                <p className="text-sm text-green-300 font-semibold">💰 Optimisation fiscale significative</p>
              </div>
            </div>

            {/* Événement 4 - Naissance */}
            <div className="glass p-6 rounded-2xl bg-white/5 border border-blue-300 hover:border-green-400 transition-all hover:scale-105">
              <div className="flex justify-center mb-4">
                <Heart className="text-pink-400" size={48} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 text-center">Naissance/Agrandissement Famille</h3>
              <p className="text-blue-200 text-sm mb-4 text-center">2-3 séances sur 6 mois</p>
              <ul className="space-y-2 text-sm text-white">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Restructuration budget familial</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Optimisation allocations familiales</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Adaptation 3e pilier et assurances</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Planification charges futures (études, etc.)</span>
                </li>
              </ul>
              <div className="mt-4 bg-green-500/20 border border-green-400 rounded-lg p-3">
                <p className="text-sm text-green-300 font-semibold">💰 Économies annuelles optimisées</p>
              </div>
            </div>

            {/* Événement 5 - Transition Professionnelle */}
            <div className="glass p-6 rounded-2xl bg-white/5 border border-blue-300 hover:border-green-400 transition-all hover:scale-105 md:col-span-2 lg:col-span-1">
              <div className="flex justify-center mb-4">
                <RefreshCw className="text-cyan-400" size={48} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 text-center">Transition Professionnelle</h3>
              <p className="text-blue-200 text-sm mb-4 text-center">2 séances sur 3 mois</p>
              <ul className="space-y-2 text-sm text-white">
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Optimisation transfert LPP</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Négociation salaire nouveau poste</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Comparaison package total (brut vs net)</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <span>Adaptation budget transition</span>
                </li>
              </ul>
              <div className="mt-4 bg-blue-500/20 border border-blue-400 rounded-lg p-3">
                <p className="text-xs text-blue-300 font-semibold">🤝 Image employeur :</p>
                <p className="text-sm text-white">Alumni satisfaits = ambassadeurs positifs</p>
              </div>
            </div>

          </div>

          {/* Pourquoi c'est puissant */}
          <div className="glass p-8 rounded-2xl bg-gradient-to-r from-green-500/20 to-blue-500/20 border-2 border-green-400">
            <div className="flex items-center justify-center mb-6">
              <Target className="text-green-400 mr-3" size={32} />
              <h3 className="text-2xl font-bold text-white text-center">
                Pourquoi ce programme change tout ?
              </h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Shield className="text-orange-400" size={48} />
                </div>
                <h4 className="font-bold text-white mb-2">Moments de vulnérabilité</h4>
                <p className="text-sm text-blue-200">
                  C'est dans ces moments que le soutien compte le plus. Absentéisme et démissions évités.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <TrendingUp className="text-green-400" size={48} />
                </div>
                <h4 className="font-bold text-white mb-2">Fidélisation exceptionnelle</h4>
                <p className="text-sm text-blue-200">
                  Un employé accompagné dans un moment difficile ne l'oublie jamais. Loyauté renforcée.
                </p>
              </div>
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Rocket className="text-blue-400" size={48} />
                </div>
                <h4 className="font-bold text-white mb-2">Différenciation totale</h4>
                <p className="text-sm text-blue-200">
                  Aucun concurrent n'offre ce niveau d'accompagnement. Avantage RH unique sur le marché.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-8">
            <p className="text-blue-200 text-sm md:text-base mb-4">
              <strong>Simple :</strong> Vos collaborateurs signalent l'événement → Nous les accompagnons sous 48h
            </p>
          </div>

        </div>
      </section>

      {/* Section Programme d'Accompagnement Stratégique */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="inline-block bg-blue-50 text-blue-700 px-4 py-2 rounded-full font-semibold text-sm mb-4">
              Programme d'accompagnement
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Démarrez avec un diagnostic personnalisé
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Avant toute décision, nous analysons ensemble le niveau de stress financier dans votre organisation et construisons une approche sur mesure.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Colonne 1 : Le diagnostic */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 shadow-sm">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Target className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Diagnostic initial offert
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Sans engagement • Durée : 45 minutes
                  </p>
                </div>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Check className="text-green-600" size={14} />
                  </div>
                  <p className="text-gray-700 text-sm">
                    <strong>Analyse de votre contexte</strong> : taille, secteur, enjeux RH actuels
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Check className="text-green-600" size={14} />
                  </div>
                  <p className="text-gray-700 text-sm">
                    <strong>Recommandations adaptées</strong> à votre situation spécifique
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <Check className="text-green-600" size={14} />
                  </div>
                  <p className="text-gray-700 text-sm">
                    <strong>Proposition tarifaire détaillée</strong> et plan de déploiement
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                <p className="text-sm text-gray-700">
                  <strong className="text-blue-900">À l'issue du diagnostic</strong>, vous disposez de tous les éléments pour décider si MindFinance correspond à vos besoins.
                </p>
              </div>
            </div>

            {/* Colonne 2 : L'approche */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 shadow-sm">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Users className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Mise en place du programme
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Déploiement immédiat et accompagnement continu
                  </p>
                </div>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                      1
                    </div>
                    <h4 className="font-bold text-gray-900">Déploiement</h4>
                  </div>
                  <p className="text-sm text-gray-600 ml-11">
                    Mise en place du programme MindFinance pour l'ensemble de vos collaborateurs avec accès immédiat à tous les services.
                  </p>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                      2
                    </div>
                    <h4 className="font-bold text-gray-900">Suivi continu</h4>
                  </div>
                  <p className="text-sm text-gray-600 ml-11">
                    Feed-back régulier sur l'utilisation du programme et ajustements selon vos besoins spécifiques.
                  </p>
                </div>

                <div>
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                      3
                    </div>
                    <h4 className="font-bold text-gray-900">Optimisation</h4>
                  </div>
                  <p className="text-sm text-gray-600 ml-11">
                    Amélioration continue du programme en fonction des retours terrain et de l'évolution de vos équipes.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600">
                  <strong>💡 Notre engagement</strong> : Nous restons à vos côtés tout au long du programme pour garantir son succès et son adoption par vos équipes.
                </p>
              </div>
            </div>
          </div>

          {/* CTA sobre */}
          <div className="text-center">
            <a 
              href="#contact" 
              className="inline-flex items-center px-8 py-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-sm"
            >
              Planifier un diagnostic stratégique
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
            <p className="text-sm text-gray-500 mt-4">
              Échange confidentiel • Sans engagement • Réponse sous 24h
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Offre personnalisée */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#0C1D3D] mb-3 md:mb-4 px-2">
              💎 Offre personnalisée selon vos besoins
            </h2>
            <p className="text-base md:text-xl text-gray-600 px-4">
              Chaque entreprise est unique. Nous adaptons notre accompagnement à votre taille et vos objectifs.
            </p>
          </div>
          
          {/* Cartes bénéfices */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="flex justify-center mb-4">
                <Target className="text-blue-500" size={48} />
              </div>
              <h3 className="text-xl font-bold text-[#0C1D3D] mb-3">Tarifs sur mesure</h3>
              <p className="text-gray-700 leading-relaxed">
                Tarification personnalisée selon la taille de votre entreprise et vos besoins spécifiques.
              </p>
            </div>
            
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="flex justify-center mb-4">
                <Users className="text-green-500" size={48} />
              </div>
              <h3 className="text-xl font-bold text-[#0C1D3D] mb-3">Tout inclus</h3>
              <p className="text-gray-700 leading-relaxed">
                Pack Sérénité complet, extension familiale, 5 grands événements de vie, et plateforme d'outils.
              </p>
            </div>
            
            <div className="glass p-6 md:p-8 rounded-2xl text-center hover:scale-105 transition-all">
              <div className="flex justify-center mb-4">
                <Shield className="text-purple-500" size={48} />
              </div>
              <h3 className="text-xl font-bold text-[#0C1D3D] mb-3">Sans engagement</h3>
              <p className="text-gray-700 leading-relaxed">
                Pas de contrat longue durée. Activation en 24h. Résiliation flexible selon vos besoins.
              </p>
            </div>
          </div>

          {/* CTA Devis */}
          <div className="glass p-8 md:p-12 rounded-2xl bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-[#0C1D3D] mb-4">
              💬 Demandez votre devis personnalisé
            </h3>
            <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Nous étudions votre situation et vous proposons une offre adaptée à votre entreprise. Réponse sous 24h.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-lg"
            >
              Demander une offre sans engagement
            </a>
          </div>
          
          {/* Avantages */}
          <div className="mt-10 text-center space-y-3">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <p className="text-gray-700 flex items-center text-sm md:text-base">
                <Check className="text-green-500 mr-2 flex-shrink-0" size={20} />
                <span>✅ Même expérience pour tous vos collaborateurs</span>
              </p>
              <p className="text-gray-700 flex items-center text-sm md:text-base">
                <Check className="text-green-500 mr-2 flex-shrink-0" size={20} />
                <span>💎 Tarifs dégressifs selon la taille</span>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <p className="text-gray-700 flex items-center text-sm md:text-base">
                <Check className="text-green-500 mr-2 flex-shrink-0" size={20} />
                <span>🚀 Activation en 24 h – sans intégration IT</span>
              </p>
              <p className="text-gray-700 flex items-center text-sm md:text-base">
                <Check className="text-green-500 mr-2 flex-shrink-0" size={20} />
                <span>💬 100 % déductible fiscalement (LIFD art. 27 / LHID art. 10)</span>
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 italic">
              Des options de communication interne ou d'ateliers sur mesure peuvent être ajoutées à la demande.
            </p>
          </div>
        </div>
      </section>

      {/* Section 6: Confidentialité */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0C1D3D] mb-6">
              Confidentialité totale & label employeur exclusif
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Les échanges entre ATOM et les collaborateurs sont <strong>strictement confidentiels</strong>, les données hébergées en Suisse selon la LPD.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Les entreprises partenaires obtiennent le <strong>label "MindFinance Friendly"</strong>, symbole d'un engagement concret envers la santé financière de leurs équipes.
              </p>
            </div>
            
            <div className="glass p-8 rounded-2xl text-center">
              <div className="w-40 h-40 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <Award size={80} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#0C1D3D] mb-2">
                Certified MindFinance Friendly
              </h3>
              <p className="text-gray-600">
                Label exclusif d'entreprise engagée
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section FAQ */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#0C1D3D] mb-4">
            Questions fréquentes
          </h2>
          <p className="text-center text-gray-600 text-base md:text-lg mb-12 max-w-2xl mx-auto">
            Toutes les réponses aux questions que se posent les DRH
          </p>
          
          <div className="space-y-4">
            {/* Question 1 - Confidentialité */}
            <details className="glass p-6 rounded-2xl group bg-white border border-gray-200 hover:border-blue-400 transition-colors">
              <summary className="text-lg font-semibold text-[#0C1D3D] cursor-pointer flex items-center justify-between list-none">
                <span className="pr-4">🔒 Comment garantissez-vous la confidentialité ?</span>
                <span className="text-blue-500 group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
              </summary>
              <div className="text-gray-700 mt-4 leading-relaxed text-sm md:text-base border-t border-gray-200 pt-4">
                <p className="mb-3">
                  <strong className="text-green-600">L'employeur ne reçoit AUCUNE donnée individuelle.</strong> Nous garantissons une confidentialité absolue pour vos collaborateurs.
                </p>
                <p className="text-sm text-gray-600 italic">
                  ✓ Conforme RGPD et Loi fédérale sur la protection des données (LPD)<br/>
                  ✓ Les séances sont strictement confidentielles
                </p>
              </div>
            </details>

            {/* Question 2 - Coachs */}
            <details className="glass p-6 rounded-2xl group bg-white border border-gray-200 hover:border-blue-400 transition-colors">
              <summary className="text-lg font-semibold text-[#0C1D3D] cursor-pointer flex items-center justify-between list-none">
                <span className="pr-4">👥 Qui sont les coachs financiers ?</span>
                <span className="text-blue-500 group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
              </summary>
              <div className="text-gray-700 mt-4 leading-relaxed text-sm md:text-base border-t border-gray-200 pt-4">
                <p className="mb-3">
                  Nos coachs sont les <strong>fondateurs d'ATOM Group SA</strong>, tous deux certifiés :
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-3">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-bold text-[#0C1D3D] mb-2">Joël Ott</p>
                    <ul className="text-sm space-y-1">
                      <li>✓ 16 ans Swiss Life Select</li>
                      <li>✓ Ex-Top Management Board</li>
                      <li>✓ +100 collaborateurs formés</li>
                    </ul>
                    <a href="https://www.linkedin.com/in/joelottfinance/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-xs mt-2 inline-block">
                      → Profil LinkedIn
                    </a>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-bold text-[#0C1D3D] mb-2">Quentin Mauron</p>
                    <ul className="text-sm space-y-1">
                      <li>✓ Brevet fédéral de conseiller</li>
                      <li>✓ 7 ans Swiss Life Select</li>
                      <li>✓ Manager régional (30 collaborateurs)</li>
                    </ul>
                    <a href="https://www.linkedin.com/in/quentinmauron/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-xs mt-2 inline-block">
                      → Profil LinkedIn
                    </a>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Ce ne sont pas des coachs bien-être improvisés, mais des <strong>professionnels de la finance certifiés</strong> avec plus de 24 ans d'expérience combinée.
                </p>
              </div>
            </details>

            {/* Question 3 - Langues */}
            <details className="glass p-6 rounded-2xl group bg-white border border-gray-200 hover:border-blue-400 transition-colors">
              <summary className="text-lg font-semibold text-[#0C1D3D] cursor-pointer flex items-center justify-between list-none">
                <span className="pr-4">🌍 Quelles langues sont disponibles ?</span>
                <span className="text-blue-500 group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
              </summary>
              <div className="text-gray-700 mt-4 leading-relaxed text-sm md:text-base border-t border-gray-200 pt-4">
                <p className="mb-3">
                  Le programme ATOM MindFinance est disponible dans <strong>3 langues</strong> :
                </p>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl mb-1">🇫🇷</p>
                    <p className="font-semibold text-sm">Français</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl mb-1">🇩🇪</p>
                    <p className="font-semibold text-sm">Allemand</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl mb-1">🇬🇧</p>
                    <p className="font-semibold text-sm">Anglais</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Les séances de coaching ET la plateforme en ligne sont disponibles dans toutes ces langues.
                </p>
              </div>
            </details>

            {/* Question 5 - Mesure résultats */}
            <details className="glass p-6 rounded-2xl group bg-white border border-gray-200 hover:border-blue-400 transition-colors">
              <summary className="text-lg font-semibold text-[#0C1D3D] cursor-pointer flex items-center justify-between list-none">
                <span className="pr-4">📊 Comment mesure-t-on les résultats ?</span>
                <span className="text-blue-500 group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
              </summary>
              <div className="text-gray-700 mt-4 leading-relaxed text-sm md:text-base border-t border-gray-200 pt-4">
                <p className="mb-3">
                  Nous assurons un <strong>suivi régulier</strong> avec des indicateurs clés :
                </p>
                <ul className="list-disc list-inside space-y-2 mb-3 ml-4">
                  <li><strong>Taux de participation</strong> : Combien de collaborateurs ont utilisé le service</li>
                  <li><strong>Score de satisfaction (NPS)</strong> : Note moyenne sur 10</li>
                </ul>
              </div>
            </details>

            {/* Question 6 - Extension Familiale */}
            <details className="glass p-6 rounded-2xl group bg-white border border-gray-200 hover:border-green-400 transition-colors">
              <summary className="text-lg font-semibold text-[#0C1D3D] cursor-pointer flex items-center justify-between list-none">
                <span className="pr-4">👨‍👩‍👧 Le conjoint/partenaire peut-il aussi bénéficier du coaching ?</span>
                <span className="text-blue-500 group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
              </summary>
              <div className="text-gray-700 mt-4 leading-relaxed text-sm md:text-base border-t border-gray-200 pt-4">
                <p className="mb-3">
                  <strong className="text-green-600">Oui, absolument ! Et c'est inclus sans surcoût.</strong>
                </p>
                <p className="mb-3">
                  Nous savons que <strong>les finances se gèrent souvent à deux</strong>. C'est pourquoi le conjoint ou partenaire de vie de votre collaborateur peut participer aux séances de coaching sans frais supplémentaires.
                </p>
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 rounded mb-3">
                  <p className="text-sm font-semibold text-gray-800 mb-2">✨ Avantages de l'extension familiale :</p>
                  <ul className="text-sm space-y-1 ml-4">
                    <li>✓ Vision globale des finances du foyer</li>
                    <li>✓ Décisions financières alignées en couple</li>
                    <li>✓ Impact durable sur toute la famille</li>
                    <li>✓ Cohésion renforcée autour du budget</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 italic">
                  💡 Les séances peuvent se faire individuellement ou en couple, selon les préférences de chacun.
                </p>
              </div>
            </details>

          </div>

          {/* CTA après FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Une autre question ?</p>
            <a 
              href="#contact" 
              className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transition-all shadow-lg"
            >
              Contactez-nous
            </a>
          </div>
        </div>
      </section>

      {/* Section 7: Contact Form */}
      <section id="contact" className="py-20 bg-[#0C1D3D] text-white relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à améliorer le bien-être financier de vos équipes ?
            </h2>
            <p className="text-xl text-gray-300">
              Rejoignez les premières entreprises suisses qui placent le bien-être financier au cœur de leur culture RH.
            </p>
          </div>
          
          {submitSuccess ? (
            <div className="glass p-12 rounded-3xl text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={48} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold mb-4">✅ Merci !</h3>
              <p className="text-xl text-gray-300 mb-8">
                Votre demande a bien été envoyée. Nous revenons vers vous sous 1 jour ouvrable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleDownloadPDF}
                  className="glass-button bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center justify-center"
                >
                  <Download className="mr-2" size={20} />
                  Guide RH: 7 signaux du stress financier
                </button>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="glass-button bg-blue-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-600 transition-all"
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="glass p-10 rounded-3xl">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Nom complet *</label>
                  <input
                    type="text"
                    placeholder="Prénom Nom"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Société *</label>
                  <input
                    type="text"
                    placeholder="Nom de l'entreprise"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">E-mail professionnel *</label>
                <input
                  type="email"
                  placeholder="nom@entreprise.ch"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Nombre d'employés *</label>
                  <select
                    value={formData.company_size}
                    onChange={(e) => setFormData({...formData, company_size: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="" disabled className="bg-[#0C1D3D]">Sélectionnez...</option>
                    <option value="20-49" className="bg-[#0C1D3D]">20 à 49 employés</option>
                    <option value="50-99" className="bg-[#0C1D3D]">50 à 99 employés</option>
                    <option value="100-199" className="bg-[#0C1D3D]">100 à 199 employés</option>
                    <option value="200-499" className="bg-[#0C1D3D]">200 à 499 employés</option>
                    <option value="500+" className="bg-[#0C1D3D]">500 employés ou plus</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Votre fonction *</label>
                  <select
                    value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                  >
                    <option value="" disabled className="bg-[#0C1D3D]">Sélectionnez...</option>
                    <option value="DRH" className="bg-[#0C1D3D]">DRH / Directeur RH</option>
                    <option value="Direction" className="bg-[#0C1D3D]">Direction générale / CEO</option>
                    <option value="Responsable RH" className="bg-[#0C1D3D]">Responsable / Chargé RH</option>
                    <option value="Manager" className="bg-[#0C1D3D]">Manager / Chef d'équipe</option>
                    <option value="Autre" className="bg-[#0C1D3D]">Autre fonction</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">Que souhaitez-vous ? *</label>
                <select
                  value={formData.request_type}
                  onChange={(e) => setFormData({...formData, request_type: e.target.value})}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="" disabled className="bg-[#0C1D3D]">Choisissez votre besoin...</option>
                  <option value="demo" className="bg-[#0C1D3D]">🎯 Demander une démo / présentation</option>
                  <option value="quote" className="bg-[#0C1D3D]">💰 Recevoir un devis personnalisé</option>
                  <option value="info" className="bg-[#0C1D3D]">💬 Simplement obtenir plus d'informations</option>
                  <option value="pilot_paid" className="bg-[#0C1D3D]">🧪 Démarrer avec un groupe pilote (10-20 personnes)</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">Message (optionnel)</label>
                <textarea
                  placeholder="Informations complémentaires (timing, contexte spécifique, questions...)"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="4"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
              
              <div className="mb-6">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.consent}
                    onChange={(e) => setFormData({...formData, consent: e.target.checked})}
                    required
                    className="mt-1 mr-3"
                  />
                  <span className="text-sm text-gray-300">
                    J'autorise ATOM Group SA à me contacter au sujet d'ATOM MindFinance. Mes données sont traitées en Suisse conformément à la LPD. 
                    <a href="/confidentialite" className="text-blue-400 hover:underline ml-1">Politique de confidentialité</a>.
                  </span>
                </label>
              </div>
              
              {submitError && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
                  {submitError}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 mb-20 sm:mb-0">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 glass-button bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center relative z-20"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Demander une présentation
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  className="glass-button bg-white/10 backdrop-blur-md text-white px-6 py-4 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center justify-center"
                >
                  <Download className="mr-2" size={20} />
                  Guide RH: 7 signaux du stress financier
                </button>
              </div>
              
              <p className="text-center text-sm text-gray-400 mt-6">
                🕐 Réponse sous 1 jour ouvrable
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
    </>
  );
};

export default MindFinance;
